// The card document schema, shared by the browser editor and the Worker so
// that both read and write card text identically (Y.XmlFragment <-> markdown).

import { getSchema, type Extensions } from '@tiptap/core'
import { type Node as PMNode, type Schema } from '@tiptap/pm/model'
import { Document } from '@tiptap/extension-document'
import { TaskItem, TaskList } from '@tiptap/extension-list'
import { BlockMath, InlineMath } from '@tiptap/extension-mathematics'
import { TableKit } from '@tiptap/extension-table'
import { StarterKit } from '@tiptap/starter-kit'

/** A card is a title (first heading) followed by any blocks. */
export const CardDocument = Document.extend({ content: 'heading block*' })

/** What only the browser's editor adds: a click on a formula, to edit it (the schema is the same). */
export type CardEditorHooks = {
  onMathClick?: (kind: 'inline' | 'block', node: PMNode, pos: number) => void
}

export function cardExtensions(hooks: CardEditorHooks = {}): Extensions {
  const click = (kind: 'inline' | 'block') =>
    hooks.onMathClick &&
    ((node: PMNode, pos: number) => {
      hooks.onMathClick?.(kind, node, pos)
    })
  const inline = click('inline')
  const block = click('block')
  return [
    CardDocument,
    StarterKit.configure({
      document: false,
      undoRedo: false,
      // Its default node would be a heading here (the doc starts with one).
      trailingNode: false,
      heading: { levels: [1, 2, 3] },
      // Links into moot are links too (they would otherwise lose their href when shown).
      link: { protocols: ['item', 'edge', 'region', 'mat'], openOnClick: false },
    }),
    TableKit.configure({ table: { resizable: false } }),
    // `- [ ] item`: checklists, which cards otherwise dropped whole.
    TaskList,
    TaskItem.configure({ nested: true }),
    // `$…$` and `$$…$$`, drawn with KaTeX in the browser.
    inline ? CardInlineMath.configure({ onClick: inline }) : CardInlineMath,
    block ? BlockMath.configure({ onClick: block }) : BlockMath,
  ]
}

/**
 * Inline math with pandoc's rule for where `$` opens and closes: no space just
 * inside the dollars, and no digit right after the closing one. Without it,
 * prices ("US$2 and $5") would be read as a formula.
 */
const CardInlineMath = InlineMath.extend({
  markdownTokenizer: {
    name: 'inlineMath',
    level: 'inline',
    start: (src: string) => src.indexOf('$'),
    tokenize: (src: string) => {
      const match = /^\$(?![\s$])((?:\\\$|[^$\n])+?)(?<!\s)\$(?!\d)/.exec(src)
      if (!match) return undefined
      return { type: 'inlineMath', raw: match[0], latex: (match[1] ?? '').trim() }
    },
  },
})

export const cardSchema: Schema = getSchema(cardExtensions())

/** Y.Doc field holding a card's text. */
export const cardField = (id: string) => `card:${id}`
